import type { FileSegment, ImageParser, ParseResult } from './Parser';
import { getPngDescription } from './PngDescriptions';
import { ExifUtils } from './ExifUtils';

export class PngParser implements ImageParser {
  parse(data: Uint8Array): ParseResult {
    const segments: FileSegment[] = [];
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let i = 0;

    // PNG Signature: 89 50 4E 47 0D 0A 1A 0A
    const signature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
    let isPng = true;
    if (data.length < 8) isPng = false;
    else {
      for (let j = 0; j < 8; j++) {
        if (data[j] !== signature[j]) {
          isPng = false;
          break;
        }
      }
    }

    if (isPng) {
      segments.push({
        index: segments.length,
        type: 'SIG',
        name: 'PNG Signature',
        start: 0,
        payloadStart: 8,
        end: 8,
        size: 8
      });
      i = 8;
    }

    while (i < data.length) {
      if (i + 12 > data.length) break; // Incomplete chunk (Length 4 + Type 4 + CRC 4)

      const length = view.getUint32(i, false); // Big endian
      const type = this.readFourCC(data, i + 4);
      const totalSize = 12 + length; // 4 (len) + 4 (type) + len (data) + 4 (crc)

      if (i + totalSize > data.length) {
        // Truncated chunk?
        break;
      }

      segments.push({
        index: segments.length,
        type: type,
        name: type,
        start: i,
        payloadStart: i + 8, // Data starts after Len(4) + Type(4)
        end: i + totalSize,
        size: totalSize
      });

      i += totalSize;
    }

    // Extract Metadata
    const metadata: Record<string, string> = {};

    // Exif (eXIf chunk)
    const exifChunk = segments.find(s => s.name === 'eXIf');
    if (exifChunk) {
      // Payload of eXIf chunk is just Exif profile.
      // But wait, CRC is at the end of the chunk (last 4 bytes of totalSize).
      // content is from payloadStart to end-4.
      const content = data.slice(exifChunk.payloadStart, exifChunk.end - 4);
      const exifData = ExifUtils.parse(content);
      Object.assign(metadata, exifData);
    }

    // Textual data (tEXt) - Simple parsing
    // tEXt format: Keyword + null + Text
    segments.filter(s => s.name === 'tEXt').forEach(s => {
      const content = data.slice(s.payloadStart, s.end - 4);
      let nullIndex = -1;
      for (let k = 0; k < content.length; k++) {
        if (content[k] === 0) { nullIndex = k; break; }
      }
      if (nullIndex > 0) {
        const keyword = new TextDecoder('iso-8859-1').decode(content.slice(0, nullIndex));
        const text = new TextDecoder('iso-8859-1').decode(content.slice(nullIndex + 1));
        metadata[keyword] = text;
      }
    });

    // IHDR info (Width x Height)
    const ihdr = segments.find(s => s.name === 'IHDR');
    if (ihdr && ihdr.size >= 12 + 13) { // 12 overhead + 13 data
      const w = view.getUint32(ihdr.payloadStart, false);
      const h = view.getUint32(ihdr.payloadStart + 4, false);
      metadata['Dimensions'] = `${w}x${h}`;

      const bitDepth = view.getUint8(ihdr.payloadStart + 8);
      const colorType = view.getUint8(ihdr.payloadStart + 9);
      metadata['Bit Depth'] = bitDepth.toString();
      metadata['Color Type'] = colorType.toString();
    }

    return {
      segments,
      format: 'PNG',
      metadata
    };
  }

  getDescription(name: string): string {
    return getPngDescription(name);
  }

  private readFourCC(data: Uint8Array, offset: number): string {
    let s = '';
    for (let j = 0; j < 4; j++) {
      if (offset + j < data.length) {
        s += String.fromCharCode(data[offset + j]);
      }
    }
    return s;
  }
}
